/**
 * Simulation Directory - V5 Migration from SimulationSelector
 * Uses demo_ui design patterns with V4 API integration
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Clock, Target, Sparkles, TrendingUp, Filter, UserPlus, Edit, Trash2, Globe, User, MoreVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import axios from 'axios';
import { SimulationLaunchModal } from './SimulationLaunchModal';
import { InviteCandidatesModal } from './InviteCandidatesModal';
import { AppHeader } from './AppHeader';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface Simulation {
    id: string;
    title: string;
    description: string;
    difficulty: string;
    duration: string;
    role: string;
    skills_evaluated: string[];
    thumbnail_color: string;
    tags?: string[];
    type?: 'global' | 'custom';
    is_global?: boolean;
    is_editable?: boolean;
    is_deletable?: boolean;
}

interface SimulationDetails {
    id: string;
    title: string;
    description: string;
    role: string;
    primary_objective: string;
    primary_conflicts: string[];
    user_goals: string[];
    simulation_type?: string;
    enable_real_world_problems?: boolean;
}

interface SimulationDirectoryProps {
    onSimulationSelect: (sessionId: string) => void;
    onEditSimulation?: (simulationId: string, isReadOnly: boolean) => void;
    onDeleteSimulation?: (simulationId: string) => Promise<boolean>;
}

export function SimulationDirectory({ onSimulationSelect, onEditSimulation, onDeleteSimulation }: SimulationDirectoryProps) {
    const [simulations, setSimulations] = useState<Simulation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
    const [selectedRole, setSelectedRole] = useState<string>('all');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'difficulty'>('popular');

    const [showLaunchModal, setShowLaunchModal] = useState(false);
    const [selectedSimulation, setSelectedSimulation] = useState<Simulation | null>(null);
    const [selectedSimDetails, setSelectedSimDetails] = useState<SimulationDetails | null>(null);
    const [preparedSessionId, setPreparedSessionId] = useState<string | null>(null);
    const [preparingSimulation, setPreparingSimulation] = useState(false);
    const [isBackendReady, setIsBackendReady] = useState(false);

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteSimulation, setInviteSimulation] = useState<Simulation | null>(null);

    const [deleteSimulation, setDeleteSimulation] = useState<Simulation | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const abortPollingRef = useRef(false);

    useEffect(() => {
        loadSimulations();
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        if (openMenuId) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openMenuId]);

    const loadSimulations = async () => {
        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/simulations/list`, { headers: authHeaders });
            const data = response.data.data;
            const simulationsList = Array.isArray(data) ? data : data?.simulations;
            setSimulations(simulationsList || []);
            setLoading(false);
        } catch (err) {
            console.error('Failed to load simulations:', err);
            setError('Failed to load available simulations');
            setLoading(false);
        }
    };

    const allRoles = useMemo(() => {
        const roles = new Set(simulations.map(sim => sim.role));
        return Array.from(roles).sort();
    }, [simulations]);

    const allTags = useMemo(() => {
        const tags = new Set(simulations.flatMap(sim => sim.tags || []));
        return Array.from(tags).sort();
    }, [simulations]);

    const filteredSimulations = useMemo(() => {
        let filtered = simulations.filter(sim => {
            const matchesSearch = searchQuery === '' ||
                sim.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sim.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sim.skills_evaluated.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesDifficulty = selectedDifficulty === 'all' || sim.difficulty === selectedDifficulty;
            const matchesRole = selectedRole === 'all' || sim.role === selectedRole;
            const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => (sim.tags || []).includes(tag));

            return matchesSearch && matchesDifficulty && matchesRole && matchesTags;
        });

        if (sortBy === 'difficulty') {
            const difficultyOrder: Record<string, number> = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
            filtered.sort((a, b) => (difficultyOrder[a.difficulty] || 0) - (difficultyOrder[b.difficulty] || 0));
        } else if (sortBy === 'newest') {
            filtered.reverse();
        }

        return filtered;
    }, [searchQuery, selectedDifficulty, selectedRole, selectedTags, sortBy, simulations]);

    const pollInitializationStatus = async (sessionId: string, maxAttempts = 150): Promise<boolean> => {
        const authHeaders = await getAuthHeaders();
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (abortPollingRef.current) {
                console.log('Polling aborted by user');
                return false;
            }

            try {
                const statusResponse = await axios.get(`${API_BASE}/sessions/${sessionId}/initialization/status`, { headers: authHeaders });
                const status = statusResponse.data.data.status;

                if (status === 'ready') {
                    return true;
                }

                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (err) {
                console.warn(`Poll attempt ${attempt + 1} failed:`, err);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
        return false;
    };

    const handleLaunchSimulation = async (simulation: Simulation) => {
        setError(null);
        setPreparedSessionId(null);
        setPreparingSimulation(true);
        setSelectedSimulation(simulation);
        setIsBackendReady(false);
        abortPollingRef.current = false;

        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/simulations/details/${simulation.id}`, { headers: authHeaders });
            setSelectedSimDetails(response.data.data);
            setShowLaunchModal(true);

            const simResponse = await axios.post(`${API_BASE}/simulations/load/${simulation.id}`, {}, { headers: authHeaders });
            const sessionId = simResponse.data.data.simulation_state.session_id;
            setPreparedSessionId(sessionId);

            const isReady = await pollInitializationStatus(sessionId);

            if (!isReady) {
                throw new Error('Simulation initialization timed out. Please try again.');
            }

            console.log('Backend ready - navigating to simulation');
            setShowLaunchModal(false);
            setPreparingSimulation(false);
            onSimulationSelect(sessionId);
        } catch (err: any) {
            console.error('Failed to load simulation:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load simulation';
            setError(errorMessage);
            setPreparingSimulation(false);
            setShowLaunchModal(false);
        }
    };

    const handleStartSimulation = () => {
        if (preparedSessionId) {
            console.log('Starting simulation:', preparedSessionId);
            setShowLaunchModal(false);
            onSimulationSelect(preparedSessionId);
        }
    };

    const handleCloseModal = () => {
        setShowLaunchModal(false);
        setSelectedSimulation(null);
        setSelectedSimDetails(null);
        setPreparedSessionId(null);
        setPreparingSimulation(false);
        setIsBackendReady(false);
    };

    const handleCancelAndCleanup = async () => {
        console.log('Cancel button clicked, session ID:', preparedSessionId);
        abortPollingRef.current = true;
        setShowLaunchModal(false);
        setPreparingSimulation(false);

        if (preparedSessionId) {
            try {
                const authHeaders = await getAuthHeaders();
                console.log('Attempting to delete session:', preparedSessionId);
                await axios.delete(`${API_BASE}/sessions/${preparedSessionId}`, { headers: authHeaders });
                console.log('Session successfully cancelled and cleaned up:', preparedSessionId);
            } catch (err: any) {
                console.error('Failed to cleanup session:', err);
            }
        }

        handleCloseModal();
    };

    const handleOpenInviteModal = (simulation: Simulation) => {
        setInviteSimulation(simulation);
        setShowInviteModal(true);
    };

    const handleCloseInviteModal = () => {
        setShowInviteModal(false);
        setInviteSimulation(null);
    };

    const handleInviteSuccess = (count: number) => {
        console.log(`Successfully invited ${count} candidate(s)`);
    };

    const handleDeleteClick = (simulation: Simulation) => {
        setDeleteSimulation(simulation);
    };

    const handleConfirmDelete = async () => {
        if (!deleteSimulation || !onDeleteSimulation) return;
        
        setIsDeleting(true);
        try {
            const success = await onDeleteSimulation(deleteSimulation.id);
            if (success) {
                loadSimulations();
            } else {
                setError('Failed to delete simulation. You may not have permission to delete this simulation.');
            }
        } catch (err: any) {
            console.error('Failed to delete simulation:', err);
            const errorMessage = err?.response?.data?.message || 'Failed to delete simulation. Only organization admins can delete simulations.';
            setError(errorMessage);
        } finally {
            setIsDeleting(false);
            setDeleteSimulation(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteSimulation(null);
    };

    const toggleTag = (tag: string) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'Easy': return 'bg-green-100 text-green-700 border-green-200';
            case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            case 'Hard': return 'bg-red-100 text-red-700 border-red-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const getThumbnailColor = (color: string) => {
        const colors: Record<string, string> = {
            purple: 'bg-purple-100 text-purple-600',
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            orange: 'bg-orange-100 text-orange-600',
            teal: 'bg-teal-100 text-teal-600',
            pink: 'bg-pink-100 text-pink-600',
            indigo: 'bg-indigo-100 text-indigo-600',
            red: 'bg-red-100 text-red-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            cyan: 'bg-cyan-100 text-cyan-600',
            gray: 'bg-gray-100 text-gray-600',
            navy: 'bg-blue-900 text-blue-200',
        };
        return colors[color] || 'bg-gray-100 text-gray-600';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading simulations...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Navigation Header */}
            <AppHeader activePage="simulations" />

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">Simulation Library</h1>
                            <p className="text-slate-600">
                                Choose from {simulations.length} realistic scenarios designed to evaluate key competencies
                            </p>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                            <div className="text-2xl font-bold text-emerald-700">{filteredSimulations.length}</div>
                            <div className="text-xs text-emerald-600">Available</div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search simulations by title, description, or skills..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <select
                                value={selectedDifficulty}
                                onChange={(e) => setSelectedDifficulty(e.target.value)}
                                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                            >
                                <option value="all">All Difficulties</option>
                                <option value="Easy">Easy</option>
                                <option value="Medium">Medium</option>
                                <option value="Hard">Hard</option>
                            </select>

                            <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                            >
                                <option value="all">All Roles</option>
                                {allRoles.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>

                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className="px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                            >
                                <option value="popular">Most Popular</option>
                                <option value="newest">Newest First</option>
                                <option value="difficulty">By Difficulty</option>
                            </select>
                        </div>

                        {allTags.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Filter className="w-4 h-4 text-slate-500" />
                                    <span className="text-sm font-medium text-slate-700">Filter by tags:</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {allTags.map(tag => (
                                        <button
                                            key={tag}
                                            onClick={() => toggleTag(tag)}
                                            className={clsx(
                                                'px-3 py-1 rounded-full text-sm font-medium transition-all duration-200',
                                                selectedTags.includes(tag)
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            )}
                                        >
                                            {tag}
                                            {selectedTags.includes(tag) && (
                                                <span className="ml-1">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Simulations Grid */}
                    <AnimatePresence mode="wait">
                        {filteredSimulations.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="bg-white rounded-xl border border-slate-200 p-12 text-center"
                            >
                                <Filter className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No simulations found</h3>
                                <p className="text-slate-600">
                                    Try adjusting your filters or search query
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            >
                                {filteredSimulations.map((simulation, index) => (
                                    <motion.div
                                        key={simulation.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className={clsx(
                                            "rounded-xl border-2 hover:shadow-lg transition-all duration-200 overflow-hidden group h-full flex flex-col",
                                            simulation.is_global 
                                                ? "bg-white border-slate-200 hover:border-emerald-300" 
                                                : "bg-teal-50 border-teal-300 hover:border-teal-500"
                                        )}
                                    >
                                        <div className="p-6 flex-1 flex flex-col">
                                            <div className="space-y-4 flex-1">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            {simulation.is_global ? (
                                                                <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                                                    <Globe className="w-3 h-3" />
                                                                    Global
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-teal-100 text-teal-700 border border-teal-200">
                                                                    <User className="w-3 h-3" />
                                                                    Custom
                                                                </span>
                                                            )}
                                                            <span className={clsx('px-2 py-1 rounded text-xs font-semibold border', getDifficultyColor(simulation.difficulty))}>
                                                                {simulation.difficulty}
                                                            </span>
                                                            <span className="flex items-center gap-1 text-xs text-slate-500">
                                                                <Clock className="w-3 h-3" />
                                                                {simulation.duration}
                                                            </span>
                                                        </div>
                                                        <h3 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">
                                                            {simulation.title}
                                                        </h3>
                                                        <p className="text-sm text-emerald-600 font-medium mb-2">
                                                            Role: {simulation.role}
                                                        </p>
                                                    </div>
                                                    <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0', getThumbnailColor(simulation.thumbnail_color))}>
                                                        <Target className="w-6 h-6" />
                                                    </div>
                                                </div>

                                                <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                                                    {simulation.description}
                                                </p>

                                                {simulation.tags && simulation.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {simulation.tags.map(tag => (
                                                            <span
                                                                key={tag}
                                                                className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="border-t border-slate-100 pt-4">
                                                    <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                                                        <Sparkles className="w-3 h-3" />
                                                        Skills Evaluated:
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {simulation.skills_evaluated.slice(0, 4).map(skill => (
                                                            <span
                                                                key={skill}
                                                                className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium border border-emerald-200"
                                                            >
                                                                {skill}
                                                            </span>
                                                        ))}
                                                        {simulation.skills_evaluated.length > 4 && (
                                                            <span className="px-2 py-1 bg-slate-50 text-slate-500 rounded text-xs">
                                                                +{simulation.skills_evaluated.length - 4} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex gap-2 mt-4">
                                                <button
                                                    onClick={() => handleOpenInviteModal(simulation)}
                                                    disabled={preparingSimulation}
                                                    className="flex-shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Invite external candidates"
                                                >
                                                    <UserPlus className="w-4 h-4" />
                                                    Invite
                                                </button>
                                                <button
                                                    onClick={() => handleLaunchSimulation(simulation)}
                                                    disabled={preparingSimulation}
                                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <TrendingUp className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                                    Launch
                                                </button>
                                                {(onEditSimulation || onDeleteSimulation) && (
                                                    <div className="relative">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenMenuId(openMenuId === simulation.id ? null : simulation.id);
                                                            }}
                                                            className="flex-shrink-0 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium h-[46px] w-[46px] rounded-lg transition-colors duration-200 flex items-center justify-center"
                                                            title="More options"
                                                        >
                                                            <MoreVertical className="w-5 h-5" />
                                                        </button>
                                                        {openMenuId === simulation.id && (
                                                            <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                                                                {onEditSimulation && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenMenuId(null);
                                                                            onEditSimulation(simulation.id, simulation.is_global || false);
                                                                        }}
                                                                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                                                    >
                                                                        <Edit className="w-4 h-4" />
                                                                        {simulation.is_global ? 'Use as Template' : 'Edit Simulation'}
                                                                    </button>
                                                                )}
                                                                {!simulation.is_global && onDeleteSimulation && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenMenuId(null);
                                                                            handleDeleteClick(simulation);
                                                                        }}
                                                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                        Delete Simulation
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </main>

            {/* Launch Modal */}
            {showLaunchModal && selectedSimulation && (
                <SimulationLaunchModal
                    simulation={selectedSimulation}
                    simulationDetails={selectedSimDetails}
                    isPreparing={preparingSimulation}
                    isReady={isBackendReady}
                    onClose={handleCloseModal}
                    onStart={handleStartSimulation}
                    onCancel={handleCancelAndCleanup}
                />
            )}

            {/* Invite Candidates Modal */}
            {inviteSimulation && (
                <InviteCandidatesModal
                    simulation={inviteSimulation}
                    isOpen={showInviteModal}
                    onClose={handleCloseInviteModal}
                    onSuccess={handleInviteSuccess}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteSimulation && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
                    >
                        <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Simulation</h3>
                        <p className="text-slate-600 mb-6">
                            Are you sure you want to delete "{deleteSimulation.title}"? This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
